import { Component, Inject, OnInit, Optional } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { CmpsDialog } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import { Permission, SecurityScope } from '../../auth/auth.model';
import { AuthService } from '../../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { SearchUserContainerComponent } from '../../user/search-user.container.component';
import { User } from '../../user/user.model';
import { WidgetComponentConfig } from '../../widget/widget.component';
import { CMPS_WIDGET_COMPONENT_CONFIG } from '../../widget/widget.metadata';
import { TranslationKeyMap } from '../coaching-insight.i18n';
import { FLOAPerson } from '../oa/models/floa.model';
import { StickyHeaderService } from '../stickyheader/stickyheader.service';

import {
    clearState,
    reload,
    selectKpi,
    selectPeriodType,
    selectUser,
} from './insight-widget.action';
import coachingInsightReducer from './insight-widget.reducer';
import { RootState } from './insight-widget.state';
import { KpiType } from './model/kpi-type.model';
import { SelectionChangeEvent } from './model/selection-change-event.model';

@Component({
    selector: '[cmpsWidgetCoachingInsightNext]',
    templateUrl: './insight-widget.component.html',
    host: {
        'data-e2e': 'component-coaching-insight',
    },
    providers: [
        // yes this needs to be provided at the component level
        StickyHeaderService,
    ],
})
@TakeUntilDestroy()
@UseStore('coachingInsight.root', coachingInsightReducer.coachingInsight.root)
export class CoachingInsightWidgetComponentNext
    implements OnInit, StateToProps<RootState>
{
    state: RootState;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _KpiType = KpiType;
    /** @private */ _userImage: SafeResourceUrl;
    /** @private */ _oaUser: FLOAPerson;
    /** @private */ _hideWidget: boolean;

    constructor(
        private readonly _domSanitizer: DomSanitizer,
        private readonly _authService: AuthService,
        @Optional()
        @Inject(CMPS_WIDGET_COMPONENT_CONFIG)
        public compConfig: WidgetComponentConfig,
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _dialog: CmpsDialog,
    ) {}

    ngOnInit(): void {
        if (this.state.authUserId == null) {
            useDispatcher(this, clearState({}));
        }

        this._hideWidget = !this._authService.user.hasPermission(
            Permission.VIEW,
            SecurityScope.COACHING,
        );
        let coachingInsightUserId = parseInt(
            sessionStorage.getItem('coachingInsight-userId'),
        );
        /** sessionStorage.removeItem('coachingInsight-userId'); // clear session item every load */

        // TODO revise. this is temporary means of flushing tabs after auth user has changed
        if (
            this.state.authUserId !== this._authService.user.id ||
            coachingInsightUserId == null ||
            isNaN(coachingInsightUserId)
        ) {
            coachingInsightUserId = this._authService.user.id;
            useDispatcher(this, clearState({}));
        }

        this._watchParams(coachingInsightUserId);
    }

    stateToProps(prevState?: RootState): void {
        if (prevState.user !== this.state.user) {
            this._userImage = !this.state.userImage
                ? null
                : this._domSanitizer.bypassSecurityTrustUrl(
                      this.state.userImage,
                  );
        }
    }

    /** @private */ _onKpiChange($event: SelectionChangeEvent): void {
        if (this.state.selectedPeriodType !== $event.periodType) {
            useDispatcher(
                this,
                selectPeriodType({ periodType: $event.periodType }),
            );
        }
        if (this.state.selectedKpi !== $event.kpi) {
            useDispatcher(this, selectKpi({ kpi: $event.kpi }));
        }
    }

    /** @private */ _onUserChange(userId: number): void {
        if (this.state.user.id !== userId) {
            this._reload(null, userId, null);
        }
    }

    /** @private */ _onOaUserChange(oaUser: FLOAPerson): void {
        this._oaUser = oaUser;
        sessionStorage.setItem(
            'coachingInsight-userId',
            this._oaUser.userId.toString(),
        );
    }

    /** @private */ _onMI2UserChange(oaUser: FLOAPerson): void {
        this._oaUser = oaUser;
    }

    onSelectUserClick(): void {
        const ref = this._dialog.open<SearchUserContainerComponent, any, User>(
            SearchUserContainerComponent,
            {
                data: {
                    userId: this.state.user.id,
                    scope: SecurityScope.COACHING,
                },
            },
        );
        ref.result
            .pipe(
                tap((user) => {
                    if (user != null) {
                        this._onUserChange(user.id);
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _reload(
        initUserId: number,
        userId: number,
        kpiTypeId: number,
    ): void {
        userId = !isNaN(userId) ? userId : initUserId;
        if (userId == null) {
            return;
        }

        kpiTypeId = !isNaN(kpiTypeId)
            ? kpiTypeId
            : this.state.selectedKpi.kpiTypeId;

        const userUnchanged = this.state.user && this.state.user.id === userId;
        const kpiUnchanged = this.state.selectedKpi.kpiTypeId === kpiTypeId;
        if (userUnchanged && kpiUnchanged) {
            return;
        }

        useDispatcher(
            this,
            reload({
                userId,
                periodTypeId: this.state.selectedPeriodType.periodTypeId,
                periodDate: this.state.selectedPeriodType.endDate,
                kpiTypeId,
            }),
        );

        useDispatcher(
            this,
            selectUser({
                userId,
                authUserId: this._authService.user.id,
            }),
        );
    }

    private _watchParams(initUserId: number): void {
        this._activatedRoute.queryParams
            .pipe(
                tap((params) => {
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    const userId = parseInt(params['userId']);
                    // eslint-disable-next-line @typescript-eslint/dot-notation
                    const kpiTypeId = parseInt(params['kpiTypeId']);

                    this._reload(initUserId, userId, kpiTypeId);

                    initUserId = null;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
